import React, { useState, useRef, useEffect } from 'react';
import { doc, deleteDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';

function Task({ task, topics, onTaskChange }) {
  // State management for UI interactions
  const [isDeleting, setIsDeleting] = useState(false);
  const [editedText, setEditedText] = useState(task.text);
  const [isFocused, setIsFocused] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isTopicDropdownOpen, setIsTopicDropdownOpen] = useState(false);
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  
  // Refs for textarea and debouncing
  const textareaRef = useRef(null);
  const debouncedUpdate = useRef(null);
  const dropdownRef = useRef(null);
  const statusDropdownRef = useRef(null);
  const topicsDropdownRef = useRef(null);

  // Add new useEffect for handling textarea height
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  }, [editedText]);

  // Handles text changes with debounced auto-save
  const handleTextChange = (e) => {
    setEditedText(e.target.value);
    setIsSaving(true);
    
    // Adjust height immediately on change
    e.target.style.height = 'auto';
    e.target.style.height = `${e.target.scrollHeight}px`;
    
    if (debouncedUpdate.current) {
      clearTimeout(debouncedUpdate.current);
    }

    debouncedUpdate.current = setTimeout(async () => {
      try {
        await updateDoc(doc(db, "tasks", task.id), {
          text: e.target.value,
          uid: task.uid || null,
        });
        if (onTaskChange) onTaskChange();
      } catch (error) {
        console.error("Error updating task:", error);
      } finally {
        setIsSaving(false);
      }
    }, 500);
  };

  // Updates task status in Firestore (todo/doing/done)
  const handleStatusChange = async (newStatus) => {
    try {
      await updateDoc(doc(db, "tasks", task.id), {
        status: newStatus,
        uid: task.uid || null,
      });
      if (onTaskChange) onTaskChange();
    } catch (error) {
      console.error("Error updating task status:", error);
    }
  };

  // Toggles the importance flag in Firestore
  const toggleImportance = async () => {
    try {
      await updateDoc(doc(db, "tasks", task.id), {
        importance: !task.importance,
        uid: task.uid || null,
      });
      if (onTaskChange) onTaskChange();
    } catch (error) {
      console.error("Error updating importance:", error);
    }
  };

  // Toggles the urgency flag in Firestore
  const toggleUrgency = async () => {
    try {
      await updateDoc(doc(db, "tasks", task.id), {
        urgency: !task.urgency,
        uid: task.uid || null,
      });
      if (onTaskChange) onTaskChange();
    } catch (error) {
      console.error("Error updating urgency:", error);
    }
  };

  // Handle topic selection/deselection
  const handleTopicChange = async (topicId) => {
    try {
      const newTopics = task.topics?.includes(topicId)
        ? task.topics.filter(id => id !== topicId)
        : [...(task.topics || []), topicId];
      
      await updateDoc(doc(db, "tasks", task.id), {
        topics: newTopics,
        uid: task.uid || null,
      });
      if (onTaskChange) onTaskChange();
    } catch (error) {
      console.error("Error updating topics:", error);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (topicsDropdownRef.current && !topicsDropdownRef.current.contains(event.target)) {
        setIsTopicDropdownOpen(false);
      }
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) {
        setIsStatusDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handles task deletion with loading state
  const handleDeleteTask = async (e) => {
    e.stopPropagation();
    if (isDeleting) return;
    
    try {
      setIsDeleting(true);
      await deleteDoc(doc(db, "tasks", task.id));
      if (onTaskChange) onTaskChange();
    } catch (error) {
      console.error("Error deleting task: ", error);
    } finally {
      setIsDeleting(false);
    }
  };

  // Add function to determine dropdown position
  const getDropdownPosition = () => {
    if (!statusDropdownRef.current) return {};
    
    const rect = statusDropdownRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const needsFlip = spaceBelow < 400; // 200px is approximate dropdown height
    
    return {
      [needsFlip ? 'bottom' : 'top']: '100%',
      right: 0,
    };
  };

  const getTopicsDropdownPosition = () => {
    if (!topicsDropdownRef.current) return {};
    
    const rect = topicsDropdownRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const needsFlip = spaceBelow < 200; // 200px is approximate dropdown height
    
    return {
      [needsFlip ? 'bottom' : 'top']: '100%',
      right: 0,
    };
  };

  return (
    <div className={`
      relative py-4 pl-6 pr-4 transition-all border-t border-gray-100
      ${isFocused ? 'bg-gray-50' : 'bg-white'}
    `}>
      <div className="flex gap-3">
        <div className="flex-1 min-h-[24px] flex items-center">
          <textarea
            ref={textareaRef}
            value={editedText}
            onChange={handleTextChange}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            className={`w-full bg-transparent border-none focus:outline-none resize-none overflow-hidden
              ${task.status === 'done' ? 'line-through text-gray-500' : ''}`}
            style={{ minHeight: '24px' }}
            rows="1"
          />
        </div>

        <div className="flex py-1 gap-2">
          
          {/* Topics dropdown */}
          <div className="relative" ref={topicsDropdownRef}>
            <button
              onClick={() => setIsTopicDropdownOpen(!isTopicDropdownOpen)}
              className="h-5 w-5 rounded-full text-sm bg-gray-100 text-gray-600 justify-center flex gap-1 "
            >
              {task.topics?.length || 0}
            </button>
            
            {isTopicDropdownOpen && (
              <div 
                className="absolute z-10 w-48 bg-white rounded-md shadow-lg"
                style={getTopicsDropdownPosition()}
              >
                <div className="py-1">
                  {topics.map(topic => (
                    <div
                      key={topic.id}
                      className="px-3 py-2 text-sm hover:bg-gray-100 cursor-pointer flex items-center gap-2"
                      onClick={() => handleTopicChange(topic.id)}
                    >
                      <input
                        type="checkbox"
                        checked={task.topics?.includes(topic.id)}
                        onChange={() => {}}
                        className="h-4 w-4"
                      />
                      {topic.name}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>


          {/* Importance toggle */}
          <button
            onClick={toggleImportance}
            className={`w-5 h-5 text-lg leading-none flex items-center justify-center ${task.importance ? 'text-yellow-500' : 'text-gray-300'}`}
            title="Important"
          >
            ★
          </button>

          {/* Urgency toggle */}
          <button
            onClick={toggleUrgency}
            className={`w-5 h-5 text-sm leading-none flex items-center justify-center ${task.urgency ? 'text-red-500 hue-rotate-[330deg]' : 'text-gray-300 grayscale opacity-30'}`}
            title="Urgent"
          >
            🔥
          </button>
          
          {/* Status button and dropdown */}
          <div ref={statusDropdownRef} className="flex ">
            <button
              onClick={() => setIsStatusDropdownOpen(!isStatusDropdownOpen)}
              className={`
                w-5 h-5 rounded-full cursor-pointer focus:outline-none border-2 border-gray-400
                ${task.status === 'done' ? 'bg-gray-500 border-gray-500' : ''}
                ${task.status === 'doing' ? 'bg-gradient-to-r from-gray-500 from-50% to-white to-50%' : ''}
                ${task.status === 'todo' ? 'bg-white border-dotted ' : ''}
              `}
              viewBox="0 0 20 20"
            />
            
            {isStatusDropdownOpen && (
              <div 
                className="absolute z-10 w-48 bg-white rounded-md shadow-lg"
                style={getDropdownPosition()}
              >
                <div
                  className="px-3 py-2 text-sm hover:bg-gray-100 cursor-pointer flex items-center gap-2"
                  onClick={() => {
                    handleStatusChange('todo');
                    setIsStatusDropdownOpen(false);
                  }}
                >
                  <div className="w-4 h-4 rounded-full border-2 border-dotted border-gray-400 bg-white" />
                  To Do
                </div>
                <div
                  className="px-3 py-2 text-sm hover:bg-gray-100 cursor-pointer flex items-center gap-2"
                  onClick={() => {
                    handleStatusChange('doing');
                    setIsStatusDropdownOpen(false);
                  }}
                >
                  <div className="w-4 h-4 rounded-full border-2 border-gray-400 bg-gradient-to-r from-gray-400 from-50% to-white to-50%" />
                  Doing
                </div>
                <div
                  className="px-3 py-2 text-sm hover:bg-gray-100 cursor-pointer flex items-center gap-2"
                  onClick={() => {
                    handleStatusChange('done');
                    setIsStatusDropdownOpen(false);
                  }}
                >
                  <div className="w-4 h-4 rounded-full border-2 border-gray-400 bg-gray-400" />
                  Done
                </div>
                <div
                  className="px-3 py-2 text-sm hover:bg-gray-100 cursor-pointer flex items-center gap-2 text-red-600"
                  onClick={(e) => {
                    handleDeleteTask(e);
                    setIsStatusDropdownOpen(false);
                  }}
                >
                  <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  Delete
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Task; 